/**
 * API ماژول کار — پوشش کامل وب‌سرویس‌های work.txt
 */
import { getApiUrl, getApiUrlWithParams } from '../config/api';
import { apiRequest, downloadExportFile } from './api';
import { uploadTaskFilesChunked, uploadSubTaskFilesChunked } from './chunkFileUpload';
import { formatWorkQuickActionRowIds } from './workPayloadBuilders';
import { finalizeWorkListFilters } from './workResponseUtils';

const qs = (params = {}) => {
  const clean = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') clean[k] = v;
  });
  return clean;
};

const json = (body) => ({ method: 'POST', body: JSON.stringify(body) });
const jsonPut = (body) => ({ method: 'PUT', body: JSON.stringify(body) });
const jsonPatch = (body) => ({ method: 'PATCH', body: JSON.stringify(body) });

const formData = (fields, filesField = null, files = []) => {
  const fd = new FormData();
  Object.entries(fields || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) fd.append(k, v);
  });
  if (filesField && files?.length) {
    files.forEach((f) => fd.append(filesField, f));
  }
  return fd;
};

// ═══════════════════════════════════════════════════════════
// قرارداد — زیرمنوها
// ═══════════════════════════════════════════════════════════

export const contractTypesApi = {
  getCreateData: () => apiRequest(getApiUrl('contract-types/create')),
  create: (name) => apiRequest(getApiUrl('contract-types'), json({ name })),
  update: (id, name) => apiRequest(getApiUrl(`contract-types/${id}`), jsonPut({ name })),
  delete: (id) => apiRequest(getApiUrl(`contract-types/${id}`), { method: 'DELETE' }),
};

export const contractTemplatesApi = {
  getAll: (params = {}) =>
    apiRequest(getApiUrlWithParams('contract-template', qs({ page: 1, per_page: 10, ...params }))),
  getCreateData: () => apiRequest(getApiUrl('contract-template/create')),
  create: (body) => apiRequest(getApiUrl('contract-template'), json(body)),
  getById: (id) => apiRequest(getApiUrl(`contract-template/${id}`)),
  getEditData: (id) => apiRequest(getApiUrl(`contract-template/edit/${id}`)),
  update: (id, body) => apiRequest(getApiUrl(`contract-template/${id}`), jsonPut(body)),
  delete: (id) => apiRequest(getApiUrl(`contract-template/${id}`), { method: 'DELETE' }),
  applyQuickAction: (action_type, row_ids) =>
    apiRequest(
      getApiUrl('contract-template/apply-quick-action'),
      json({ action_type, row_ids: formatWorkQuickActionRowIds(row_ids) }),
    ),
};

export const contractDiscussionsApi = {
  create: (body) => apiRequest(getApiUrl('contract-discussions'), json(body)),
  getEditData: (id) => apiRequest(getApiUrl(`contract-discussions/edit/${id}`)),
  update: (id, body) => apiRequest(getApiUrl(`contract-discussions/${id}`), jsonPut(body)),
  delete: (id) => apiRequest(getApiUrl(`contract-discussions/${id}`), { method: 'DELETE' }),
};

export const contractFilesApi = {
  upload: (contract_id, files) =>
    apiRequest(
      getApiUrl('contract-files'),
      { method: 'POST', body: formData({ contract_id }, 'file[]', files) },
    ),
  delete: (id) => apiRequest(getApiUrl(`contract-files/${id}`), { method: 'DELETE' }),
  downloadUrl: (id) => getApiUrl(`contract-files/download/${id}`),
  download: (id, filename) =>
    downloadExportFile(getApiUrl(`contract-files/download/${id}`), {
      filenameFallback: filename || `contract-file-${id}`,
    }),
};

export const contractRenewApi = {
  create: (body) => apiRequest(getApiUrl('contract-renew'), json(body)),
  getEditData: (id) => apiRequest(getApiUrl(`contract-renew/edit/${id}`)),
  update: (id, body) => apiRequest(getApiUrl(`contract-renew/${id}`), jsonPut(body)),
  delete: (id) => apiRequest(getApiUrl(`contract-renew/${id}`), { method: 'DELETE' }),
};

export const contractsApi = {
  getAll: (filters = {}) =>
    apiRequest(
      getApiUrlWithParams(
        'contracts',
        qs(
          finalizeWorkListFilters(
            {
              startDate: filters.startDate,
              endDate: filters.endDate,
              client_id: filters.client_id,
              contract_type: filters.contract_type,
              project_id: filters.project_id,
              search: filters.search,
              page: filters.page ?? 1,
              per_page: filters.per_page ?? 10,
            },
            'contracts',
          ),
        ),
      ),
    ),
  getCreateData: () => apiRequest(getApiUrl('contracts/create')),
  create: (body) => apiRequest(getApiUrl('contracts'), json(body)),
  getById: (id) => apiRequest(getApiUrl(`contracts/${id}`)),
  getEditData: (id) => apiRequest(getApiUrl(`contracts/edit/${id}`)),
  update: (id, body) => apiRequest(getApiUrl(`contracts/${id}`), jsonPut(body)),
  delete: (id) => apiRequest(getApiUrl(`contracts/${id}`), { method: 'DELETE' }),
  applyQuickAction: (action_type, row_ids) =>
    apiRequest(
      getApiUrl('contracts/apply-quick-action'),
      json({
        action_type,
        row_ids: formatWorkQuickActionRowIds(row_ids),
      }),
    ),
  sign: (id, imageFile) => {
    const fd = new FormData();
    if (imageFile) fd.append('image', imageFile);
    return apiRequest(getApiUrl(`company-sign/sign/${id}`), { method: 'POST', body: fd });
  },
  download: (id, filename) =>
    downloadExportFile(getApiUrl(`contracts/download/${id}`), {
      filenameFallback: filename || `contract-${id}.pdf`,
    }),
  exportList: (filters = {}) => {
    const toExportDate = (v) => {
      if (v == null || v === '') return undefined;
      return String(v).replace(/\//g, '-');
    };
    return downloadExportFile(
      getApiUrlWithParams(
        'contracts/export',
        qs({
          startDate: toExportDate(filters.startDate),
          endDate: toExportDate(filters.endDate),
          client_id: filters.client_id ?? 'all',
          signed: filters.signed ?? 'all',
          contract_type: filters.contract_type ?? 'all',
          project_id: filters.project_id ?? 'all',
          search: filters.search,
        }),
      ),
      { filenameFallback: 'contracts.xlsx' },
    );
  },
};

// ═══════════════════════════════════════════════════════════
// پروژه — زیرمنوها
// ═══════════════════════════════════════════════════════════

export const projectCategoriesApi = {
  getCreateData: () => apiRequest(getApiUrl('project-category/create')),
  create: (category_name) => apiRequest(getApiUrl('project-category'), json({ category_name })),
  update: (id, category_name) =>
    apiRequest(getApiUrl(`project-category/${id}`), jsonPut({ category_name })),
  delete: (id) => apiRequest(getApiUrl(`project-category/${id}`), { method: 'DELETE' }),
};

export const projectSubcategoriesApi = {
  getCreateData: () => apiRequest(getApiUrl('project-subcategory/create')),
  create: (body) => apiRequest(getApiUrl('project-subcategory'), json(body)),
  update: (id, body) => apiRequest(getApiUrl(`project-subcategory/${id}`), jsonPut(body)),
  delete: (id) => apiRequest(getApiUrl(`project-subcategory/${id}`), { method: 'DELETE' }),
  getByCategory: (categoryId) => apiRequest(getApiUrl(`get_project_sub_category/${categoryId}`)),
};

export const projectTemplatesApi = {
  getAll: (params = {}) =>
    apiRequest(
      getApiUrlWithParams(
        'project-template',
        qs(
          finalizeWorkListFilters(
            { page: 1, per_page: 10, ...params },
            'projectTemplates',
          ),
        ),
      ),
    ),
  getCreateData: () => apiRequest(getApiUrl('project-template/create')),
  create: (body) => apiRequest(getApiUrl('project-template'), json(body)),
  getById: (id) => apiRequest(getApiUrl(`project-template/${id}`)),
  getEditData: (id) => apiRequest(getApiUrl(`project-template/edit/${id}`)),
  update: (id, body) => apiRequest(getApiUrl(`project-template/${id}`), jsonPut(body)),
  delete: (id) => apiRequest(getApiUrl(`project-template/${id}`), { method: 'DELETE' }),
  applyQuickAction: (action_type, row_ids) =>
    apiRequest(
      getApiUrl('project-template/apply-quick-action'),
      json({ action_type, row_ids: formatWorkQuickActionRowIds(row_ids) }),
    ),
};

export const projectTemplateMembersApi = {
  getCreateData: (templateId) =>
    apiRequest(getApiUrl(`project-template-member/create/${templateId}`)),
  create: (body) => apiRequest(getApiUrl('project-template-member'), json(body)),
  delete: (id) => apiRequest(getApiUrl(`project-template-member/${id}`), { method: 'DELETE' }),
  saveGroup: (body) =>
    apiRequest(getApiUrl('project-template-members/save-group'), json(body)),
};

export const projectTemplateTasksApi = {
  /** project_id در query = شناسه الگوی پروژه (طبق work.txt) */
  getCreateData: (templateId) =>
    apiRequest(
      getApiUrlWithParams('project-template-task/create', {
        project_id: templateId,
      }),
    ),
  create: (body) => apiRequest(getApiUrl('project-template-task'), json(body)),
  getById: (id) => apiRequest(getApiUrl(`project-template-task/${id}`)),
  getEditData: (id) => apiRequest(getApiUrl(`project-template-task/edit/${id}`)),
  update: (id, body) => apiRequest(getApiUrl(`project-template-task/${id}`), jsonPut(body)),
  delete: (id) => apiRequest(getApiUrl(`project-template-task/${id}`), { method: 'DELETE' }),
};

export const projectTemplateMilestonesApi = {
  getCreateData: (templateId) =>
    apiRequest(getApiUrl(`project-template-milestone/create/${templateId}`)),
  create: (body) => apiRequest(getApiUrl('project-template-milestone'), json(body)),
  getById: (id) => apiRequest(getApiUrl(`project-template-milestone/${id}`)),
  getEditData: (id) => apiRequest(getApiUrl(`project-template-milestone/edit/${id}`)),
  update: (id, body) => apiRequest(getApiUrl(`project-template-milestone/${id}`), jsonPut(body)),
  delete: (id) => apiRequest(getApiUrl(`project-template-milestone/${id}`), { method: 'DELETE' }),
  updateStatus: (id, status) =>
    apiRequest(getApiUrl(`project-template-milestone/update-status/${id}`), jsonPatch({ status })),
};

export const projectFilesApi = {
  upload: (project_id, files) =>
    apiRequest(
      getApiUrl('project-files'),
      { method: 'POST', body: formData({ project_id }, 'file[]', files) },
    ),
  delete: (id) => apiRequest(getApiUrl(`project-files/${id}`), { method: 'DELETE' }),
  downloadUrl: (id) => getApiUrl(`project-files/download/${id}`),
};

export const projectMembersApi = {
  getCreateData: (projectId) =>
    apiRequest(getApiUrl(`projects/${projectId}/members/create`)),
  create: (body) => apiRequest(getApiUrl('project-members'), json(body)),
  updateHourlyRate: (id, hourly_rate) =>
    apiRequest(getApiUrl(`project-members/${id}`), jsonPut({ hourly_rate })),
  delete: (id) => apiRequest(getApiUrl(`project-members/${id}`), { method: 'DELETE' }),
  saveGroup: (body) => apiRequest(getApiUrl('project-members/save-group'), json(body)),
};

export const projectMilestonesApi = {
  getCreateData: (projectId) =>
    apiRequest(getApiUrl(`project-milestones/create/${projectId}`)),
  create: (body) => apiRequest(getApiUrl('project-milestones'), json(body)),
  getEditData: (id) => apiRequest(getApiUrl(`project-milestones/edit/${id}`)),
  update: (id, body) => apiRequest(getApiUrl(`project-milestones/${id}`), jsonPut(body)),
  delete: (id) => apiRequest(getApiUrl(`project-milestones/${id}`), { method: 'DELETE' }),
  updateStatus: (id, status) =>
    apiRequest(getApiUrl(`project-milestones/update-status/${id}`), jsonPatch({ status })),
};

export const discussionCategoriesApi = {
  getCreateData: () => apiRequest(getApiUrl('discussion-category/create')),
  create: (body) => apiRequest(getApiUrl('discussion-category'), json(body)),
  update: (id, body) => apiRequest(getApiUrl(`discussion-category/${id}`), jsonPut(body)),
  delete: (id) => apiRequest(getApiUrl(`discussion-category/${id}`), { method: 'DELETE' }),
};

export const discussionsApi = {
  getCreateData: (projectId) => apiRequest(getApiUrl(`discussion/create/${projectId}`)),
  create: (body, files = []) => {
    const fd = new FormData();
    fd.append('title', body.title ?? '');
    if (body.discussion_category != null && body.discussion_category !== '') {
      fd.append('discussion_category', String(body.discussion_category));
    }
    fd.append('project_id', String(body.project_id));
    fd.append('description', body.description ?? '');
    (files || []).forEach((f) => fd.append('file[]', f));
    return apiRequest(getApiUrl('discussion'), { method: 'POST', body: fd });
  },
  getById: (id) => apiRequest(getApiUrl(`discussion/${id}`)),
  delete: (id) => apiRequest(getApiUrl(`discussion/${id}`), { method: 'DELETE' }),
};

export const discussionRepliesApi = {
  create: (body, files = []) => {
    const fd = new FormData();
    fd.append('discussion_id', body.discussion_id);
    fd.append('description', body.description);
    (files || []).forEach((f) => fd.append('file[]', f));
    return apiRequest(getApiUrl('discussion-reply'), { method: 'POST', body: fd });
  },
  delete: (id) => apiRequest(getApiUrl(`discussion-reply/${id}`), { method: 'DELETE' }),
  setBestAnswer: (body) => apiRequest(getApiUrl('discussion/set-best-answer'), json(body)),
};

export const discussionFilesApi = {
  delete: (id) => apiRequest(getApiUrl(`discussion-files/${id}`), { method: 'DELETE' }),
  downloadUrl: (id) => getApiUrl(`discussion-files/download/${id}`),
};

export const projectNotesApi = {
  getCreateData: (projectId) => apiRequest(getApiUrl(`project-notes/create/${projectId}`)),
  create: (body) => apiRequest(getApiUrl('project-notes'), json(body)),
  getById: (id) => apiRequest(getApiUrl(`project-notes/${id}`)),
  getEditData: (id) => apiRequest(getApiUrl(`project-notes/edit/${id}`)),
  update: (id, body) => apiRequest(getApiUrl(`project-notes/${id}`), jsonPut(body)),
  delete: (id) => apiRequest(getApiUrl(`project-notes/${id}`), { method: 'DELETE' }),
  checkPassword: (body) => apiRequest(getApiUrl('project-notes/check-password'), json(body)),
  exportList: (projectId) =>
    downloadExportFile(
      getApiUrlWithParams('project-notes/export', { projectID: projectId }),
      { filenameFallback: `project-notes-${projectId}.xlsx` },
    ),
};

export const projectRatingsApi = {
  create: (body) => apiRequest(getApiUrl('project-ratings'), json(body)),
  update: (id, body) => apiRequest(getApiUrl(`project-ratings/${id}`), jsonPut(body)),
  delete: (id) => apiRequest(getApiUrl(`project-ratings/${id}`), { method: 'DELETE' }),
};

export const projectsApi = {
  getAll: (filters = {}) =>
    apiRequest(
      getApiUrlWithParams(
        'projects',
        qs(
          finalizeWorkListFilters(
            {
              startDate: filters.startDate,
              endDate: filters.endDate,
              status: filters.status,
              date_filter_on: filters.date_filter_on ?? 'start_date',
              client_id: filters.client_id,
              category_id: filters.category_id,
              employee_id: filters.employee_id,
              team_id: filters.team_id,
              pinned: filters.pinned,
              public: filters.public,
              progress: filters.progress,
              search: filters.search,
              page: filters.page ?? 1,
              per_page: filters.per_page ?? 10,
            },
            'projects',
          ),
        ),
      ),
    ),
  getArchive: (filters = {}) =>
    apiRequest(
      getApiUrlWithParams(
        'projects/archive',
        qs({
          page: filters.page ?? 1,
          per_page: filters.per_page ?? 10,
          search: filters.search,
          status: filters.status,
          client_id: filters.client_id,
          category_id: filters.category_id,
          employee_id: filters.employee_id,
          team_id: filters.team_id,
        }),
      ),
    ),
  archive: (id) => apiRequest(getApiUrl(`projects/archive/${id}`), { method: 'POST' }),
  restoreArchive: (id) =>
    apiRequest(getApiUrl(`projects/archive-restore/${id}`), { method: 'POST' }),
  storePin: (project_id) =>
    apiRequest(getApiUrl('projects/store-pin'), json({ project_id: Number(project_id) })),
  destroyPin: (projectId) =>
    apiRequest(getApiUrl(`projects/destroy-pin/${projectId}`), { method: 'DELETE' }),
  getCalendar: (params = {}) =>
    apiRequest(
      getApiUrlWithParams(
        'project-calendar',
        qs({
          start: params.start,
          end: params.end,
          pinned: params.pinned ?? 'all',
          team_id: params.team_id ?? 'all',
          status: params.status ?? 'all',
          employee_id: params.employee_id ?? 'all',
          category_id: params.category_id ?? 'all',
          client_id: params.client_id ?? 'all',
          search: params.search,
          'progress[]': params.progress ?? '0-20,21-40,41-60,61-80,81-99',
        }),
      ),
    ),
  exportList: (filters = {}) =>
    downloadExportFile(
      getApiUrlWithParams(
        'projects/export',
        qs(
          finalizeWorkListFilters(
            {
              startDate: filters.startDate,
              endDate: filters.endDate,
              status: filters.status ?? 'all',
              date_filter_on: filters.date_filter_on ?? 'start_date',
              client_id: filters.client_id ?? 'all',
              category_id: filters.category_id ?? 'all',
              employee_id: filters.employee_id ?? 'all',
              team_id: filters.team_id ?? 'all',
              pinned: filters.pinned ?? 'all',
              public: filters.public ?? 'all',
              progress: filters.progress,
              search: filters.search,
              page: filters.page ?? 1,
              per_page: filters.per_page ?? 10,
            },
            'projects',
          ),
        ),
      ),
      { filenameFallback: 'projects.xlsx' },
    ),
  getCreateData: (templateId) =>
    templateId
      ? apiRequest(getApiUrl(`projects/create/${templateId}`))
      : apiRequest(getApiUrl('projects/create')),
  create: (body) => apiRequest(getApiUrl('projects'), json(body)),
  getById: (id, tab) =>
    apiRequest(getApiUrlWithParams(`projects/${id}`, tab ? { tab } : {})),
  getEditData: (id) => apiRequest(getApiUrl(`projects/edit/${id}`)),
  update: (id, body) => apiRequest(getApiUrl(`projects/${id}`), jsonPut(body)),
  delete: (id) => apiRequest(getApiUrl(`projects/${id}`), { method: 'DELETE' }),
  applyQuickAction: (action_type, row_ids, extra = {}) =>
    apiRequest(
      getApiUrl('projects/apply-quick-action'),
      json({
        action_type,
        row_ids: formatWorkQuickActionRowIds(row_ids),
        ...extra,
      }),
    ),
  assignProjectAdmin: (body) =>
    apiRequest(getApiUrl('projects/assign-project-admin'), json(body)),
};

// ═══════════════════════════════════════════════════════════
// وظایف — زیرمنوها
// ═══════════════════════════════════════════════════════════

export const taskCategoriesApi = {
  getCreateData: () => apiRequest(getApiUrl('task-category/create')),
  create: (category_name) => apiRequest(getApiUrl('task-category'), json({ category_name })),
  update: (id, category_name) =>
    apiRequest(getApiUrl(`task-category/${id}`), jsonPut({ category_name })),
  delete: (id) => apiRequest(getApiUrl(`task-category/${id}`), { method: 'DELETE' }),
};

export const taskLabelsApi = {
  getCreateData: () => apiRequest(getApiUrl('task-label/create')),
  create: (body) => apiRequest(getApiUrl('task-label'), json(body)),
  update: (id, body) => apiRequest(getApiUrl(`task-label/${id}`), jsonPut(body)),
  delete: (id) => apiRequest(getApiUrl(`task-label/${id}`), { method: 'DELETE' }),
};

export const taskFilesApi = {
  upload: (task_id, files, options = {}) => uploadTaskFilesChunked(task_id, files, options),
  delete: (id) => apiRequest(getApiUrl(`task-files/${id}`), { method: 'DELETE' }),
  deleteAll: (taskId) =>
    apiRequest(getApiUrl(`task-files/delete-all-files/${taskId}`), { method: 'DELETE' }),
  downloadUrl: (id) => getApiUrl(`task-files/download/${id}`),
  downloadAllUrl: (taskId) => getApiUrl(`task-files/download-all-files/${taskId}`),
  downloadAll: (taskId) =>
    downloadExportFile(getApiUrl(`task-files/download-all-files/${taskId}`), {
      filenameFallback: `task-${taskId}-files.zip`,
    }),
  getConvertProgress: (fileId) =>
    apiRequest(getApiUrl(`task-files/task/${fileId}/convert-progress`)),
};

export const taskCommentsApi = {
  create: (body) => apiRequest(getApiUrl('task-comment'), json(body)),
  update: (id, body) => apiRequest(getApiUrl(`task-comment/${id}`), jsonPut(body)),
  delete: (id) => apiRequest(getApiUrl(`task-comment/${id}`), { method: 'DELETE' }),
  saveLike: (comment_id, emoji_name) =>
    apiRequest(getApiUrl('task-comment/save-comment-like'), json({ comment_id, emoji_name })),
};

export const taskNotesApi = {
  create: (body) => apiRequest(getApiUrl('task-note'), json(body)),
  update: (id, body) => apiRequest(getApiUrl(`task-note/${id}`), jsonPut(body)),
  delete: (id) => apiRequest(getApiUrl(`task-note/${id}`), { method: 'DELETE' }),
};

export const subTasksApi = {
  getCreateData: (taskId) =>
    apiRequest(getApiUrlWithParams('sub-tasks/create', { task_id: taskId })),
  create: (body) => apiRequest(getApiUrl('sub-tasks'), json(body)),
  update: (id, body) => apiRequest(getApiUrl(`sub-tasks/${id}`), jsonPut(body)),
  delete: (id) => apiRequest(getApiUrl(`sub-tasks/${id}`), { method: 'DELETE' }),
  changeStatus: (subtask_id, status) =>
    apiRequest(getApiUrl('sub-task/change-status'), json({ subtask_id: Number(subtask_id), status })),
};

export const subTaskFilesApi = {
  upload: (sub_task_id, files, options = {}) =>
    uploadSubTaskFilesChunked(sub_task_id, files, options),
  delete: (id) => apiRequest(getApiUrl(`sub-task-files/${id}`), { method: 'DELETE' }),
  deleteAll: (subTaskId) =>
    apiRequest(getApiUrl(`sub-task-files/delete-all-files/${subTaskId}`), { method: 'DELETE' }),
  downloadUrl: (id) => getApiUrl(`sub-task-files/download/${id}`),
  downloadAllUrl: (subTaskId) => getApiUrl(`sub-task-files/download-all-files/${subTaskId}`),
  downloadAllSubtasksUrl: (taskId) => getApiUrl(`sub-task-files/download-all-subtask-files/${taskId}`),
  getConvertProgress: (fileId) =>
    apiRequest(getApiUrl(`sub-task-files/subtask/${fileId}/convert-progress`)),
  download: (id, filename) =>
    downloadExportFile(getApiUrl(`sub-task-files/download/${id}`), {
      filenameFallback: filename || `subtask-file-${id}`,
    }),
  downloadAll: (subTaskId) =>
    downloadExportFile(getApiUrl(`sub-task-files/download-all-files/${subTaskId}`), {
      filenameFallback: `subtask-${subTaskId}-files.zip`,
    }),
  downloadAllSubtasks: (taskId) =>
    downloadExportFile(getApiUrl(`sub-task-files/download-all-subtask-files/${taskId}`), {
      filenameFallback: `task-${taskId}-subtask-files.zip`,
    }),
};

export const tasksApi = {
  getAll: (filters = {}) =>
    apiRequest(
      getApiUrlWithParams(
        'tasks',
        qs(
          finalizeWorkListFilters(
            {
              startDate: filters.startDate,
              endDate: filters.endDate,
              project_id: filters.project_id,
              client_id: filters.client_id,
              assigned_by: filters.assigned_by,
              assigned_to: filters.assigned_to,
              status: filters.status,
              label_id: filters.label_id,
              priority: filters.priority,
              category_id: filters.category_id,
              billable: filters.billable,
              milestone_id: filters.milestone_id,
              type: filters.type,
              pinned: filters.pinned,
              search: filters.search,
              page: filters.page,
              per_page: filters.per_page,
            },
            'tasks',
          ),
        ),
      ),
    ),
  getBoard: (filters = {}) =>
    apiRequest(
      getApiUrlWithParams(
        'taskboards',
        qs(
          finalizeWorkListFilters(
            {
              startDate: filters.startDate,
              endDate: filters.endDate,
              project_id: filters.project_id,
              client_id: filters.client_id,
              assigned_by: filters.assigned_by,
              assigned_to: filters.assigned_to,
              status: filters.status,
              label_id: filters.label_id,
              priority: filters.priority,
              category_id: filters.category_id,
              billable: filters.billable,
              milestone_id: filters.milestone_id,
              type: filters.type,
              pinned: filters.pinned,
              search: filters.search,
            },
            'tasks',
          ),
        ),
      ),
    ),
  loadMoreBoard: (filters = {}) =>
    apiRequest(
      getApiUrlWithParams(
        'taskboards/load-more',
        qs(
          finalizeWorkListFilters(
            {
              startDate: filters.startDate,
              endDate: filters.endDate,
              project_id: filters.project_id,
              client_id: filters.client_id,
              assigned_by: filters.assigned_by,
              assigned_to: filters.assigned_to,
              status: filters.status,
              label_id: filters.label_id,
              priority: filters.priority,
              category_id: filters.category_id,
              billable: filters.billable,
              milestone_id: filters.milestone_id,
              type: filters.type,
              pinned: filters.pinned,
              search: filters.search,
              currentTotalTasks: filters.currentTotalTasks,
              column_id: filters.column_id,
            },
            'tasks',
          ),
        ),
      ),
    ),
  getCreateData: () => apiRequest(getApiUrl('tasks/create')),
  create: (body) => apiRequest(getApiUrl('tasks'), json(body)),
  getById: (id) => apiRequest(getApiUrl(`tasks/${id}`)),
  getEditData: (id) => apiRequest(getApiUrl(`tasks/edit/${id}`)),
  update: (id, body) => apiRequest(getApiUrl(`tasks/${id}`), jsonPut(body)),
  updateSingleField: (id, field, value) =>
    apiRequest(getApiUrl(`tasks/update-single-field/${id}`), json({ field, value })),
  getChaproomTaskStatus: (orderId) =>
    apiRequest(getApiUrl(`tasks/chaproom-task-status/${orderId}`)),
  delete: (id) => apiRequest(getApiUrl(`tasks/${id}`), { method: 'DELETE' }),
  changeStatus: (task_id, status, extra = {}) => {
    const body = { task_id: Number(task_id), status };
    if (extra.trackingCode != null && String(extra.trackingCode).trim() !== '') {
      body.trackingCode = String(extra.trackingCode).trim();
    }
    if (extra.postageMethod != null && String(extra.postageMethod).trim() !== '') {
      body.postageMethod = String(extra.postageMethod).trim();
    }
    return apiRequest(getApiUrl('tasks/change-status'), json(body));
  },
  applyQuickAction: (payload) =>
    apiRequest(
      getApiUrl('tasks/apply-quick-action'),
      json({
        ...payload,
        row_ids: formatWorkQuickActionRowIds(payload?.row_ids),
      }),
    ),
  storePin: (task_id, project_id) =>
    apiRequest(getApiUrl('tasks/store-pin'), json({ task_id: Number(task_id), project_id: Number(project_id) })),
  destroyPin: (id) => apiRequest(getApiUrl(`tasks/destroy-pin/${id}`), { method: 'DELETE' }),
  exportList: (filters = {}) =>
    downloadExportFile(
      getApiUrlWithParams(
        'tasks/export',
        qs(
          finalizeWorkListFilters(
            {
              startDate: filters.startDate,
              endDate: filters.endDate,
              project_id: filters.project_id ?? 'all',
              client_id: filters.client_id ?? 'all',
              assigned_by: filters.assigned_by ?? 'all',
              assigned_to: filters.assigned_to ?? 'all',
              status: filters.status ?? 'all',
              label_id: filters.label_id ?? 'all',
              priority: filters.priority ?? 'all',
              category_id: filters.category_id ?? 'all',
              billable: filters.billable ?? 'all',
              milestone_id: filters.milestone_id ?? 'all',
              type: filters.type ?? 'all',
              pinned: filters.pinned ?? 'all',
              search: filters.search,
            },
            'tasks',
          ),
        ),
      ),
      { filenameFallback: 'tasks.xlsx' },
    ),
};

export const taskboardsColumnsApi = {
  create: (body) => apiRequest(getApiUrl('taskboards'), json(body)),
  getEditData: (id) => apiRequest(getApiUrl(`taskboards/edit/${id}`)),
  update: (id, body) => apiRequest(getApiUrl(`taskboards/${id}`), jsonPut(body)),
  delete: (id) => apiRequest(getApiUrl(`taskboards/${id}`), { method: 'DELETE' }),
};

/** فهرست تمام endpointها برای مرجع (همه در workApi پوشش داده شده‌اند) */
export const WORK_API_ENDPOINTS = [
  'contract-types/create', 'contract-types', 'contract-types/{id}',
  'contract-template', 'contract-template/create', 'contract-template/{id}', 'contract-template/edit/{id}', 'contract-template/apply-quick-action',
  'contract-discussions', 'contract-discussions/edit/{id}', 'contract-discussions/{id}',
  'contract-files', 'contract-files/{id}', 'contract-files/download/{id}',
  'contract-renew', 'contract-renew/edit/{id}', 'contract-renew/{id}',
  'contracts', 'contracts/create', 'contracts/{id}', 'contracts/edit/{id}', 'contracts/export', 'contracts/apply-quick-action', 'company-sign/sign/{id}',
  'project-category/create', 'project-category', 'project-category/{id}',
  'project-subcategory/create', 'project-subcategory', 'project-subcategory/{id}', 'get_project_sub_category/{id}',
  'project-template', 'project-template/create', 'project-template/{id}', 'project-template/edit/{id}', 'project-template/apply-quick-action',
  'project-template-member/create/{id}', 'project-template-member', 'project-template-member/{id}', 'project-template-members/save-group',
  'project-template-task/create', 'project-template-task', 'project-template-task/{id}', 'project-template-task/edit/{id}',
  'project-template-milestone/create/{id}', 'project-template-milestone', 'project-template-milestone/{id}', 'project-template-milestone/edit/{id}', 'project-template-milestone/update-status/{id}',
  'project-files', 'project-files/{id}', 'project-files/download/{id}',
  'discussion-category/create', 'discussion-category', 'discussion-category/{id}',
  'discussion/create/{projectId}', 'discussion', 'discussion/{id}',
  'discussion-reply', 'discussion-reply/{id}', 'discussion/set-best-answer', 'discussion-files/{id}', 'discussion-files/download/{id}',
  'project-notes/create/{projectId}', 'project-notes', 'project-notes/{id}', 'project-notes/edit/{id}', 'project-notes/check-password',
  'project-ratings', 'project-ratings/{id}',
  'projects', 'projects/create', 'projects/create/{templateId}', 'projects/{id}', 'projects/edit/{id}', 'projects/apply-quick-action',
  'projects/store-pin', 'projects/destroy-pin/{id}', 'projects/archive', 'projects/archive/{id}', 'projects/archive-restore/{id}',
  'projects/export', 'project-calendar',
  'projects/assign-project-admin',
  'task-category/create', 'task-category', 'task-category/{id}',
  'task-label/create', 'task-label', 'task-label/{id}',
  'task-files', 'task-files/{id}', 'task-files/download/{id}', 'task-files/delete-all-files/{taskId}',
  'task-files/download-all-files/{taskId}', 'task-files/task/{fileId}/convert-progress',
  'task-comment', 'task-comment/{id}', 'task-comment/save-comment-like',
  'task-note', 'task-note/{id}',
  'sub-tasks', 'sub-tasks/create', 'sub-tasks/{id}', 'sub-task/change-status',
  'sub-task-files', 'sub-task-files/{id}', 'sub-task-files/download/{id}',
  'sub-task-files/delete-all-files/{subTaskId}', 'sub-task-files/download-all-files/{subTaskId}',
  'sub-task-files/download-all-subtask-files/{taskId}',
  'sub-task-files/subtask/{fileId}/convert-progress',
  'tasks', 'tasks/create', 'tasks/{id}', 'tasks/edit/{id}', 'tasks/apply-quick-action',
  'tasks/change-status', 'tasks/update-single-field/{id}', 'tasks/store-pin', 'tasks/destroy-pin/{id}',
  'tasks/chaproom-task-status/{orderId}',
  'taskboards', 'taskboards/load-more', 'taskboards/edit/{id}', 'taskboards/{id}',
];

export default {
  contractTypesApi,
  contractTemplatesApi,
  contractDiscussionsApi,
  contractFilesApi,
  contractRenewApi,
  contractsApi,
  projectCategoriesApi,
  projectSubcategoriesApi,
  projectTemplatesApi,
  projectTemplateMembersApi,
  projectTemplateTasksApi,
  projectTemplateMilestonesApi,
  projectFilesApi,
  discussionCategoriesApi,
  discussionsApi,
  discussionRepliesApi,
  discussionFilesApi,
  projectNotesApi,
  projectRatingsApi,
  projectsApi,
  taskCategoriesApi,
  taskLabelsApi,
  taskFilesApi,
  taskCommentsApi,
  taskNotesApi,
  subTasksApi,
  subTaskFilesApi,
  tasksApi,
  taskboardsColumnsApi,
};
